import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faWandMagicSparkles, 
    faStopwatch, 
    faShieldHalved, 
    faChartLine, 
    faCircleInfo, 
    faArrowRight, 
    faCircleCheck 
} from '@fortawesome/free-solid-svg-icons';
import './SurveyIntro.css';

function SurveyIntro({ survey, lang, onStart }) {
    const { t } = useTranslation();

    const title = survey?.translations?.name?.[lang] || survey?.translations?.name?.['en'];
    const description = survey?.translations?.description?.[lang] || survey?.translations?.description?.['en'] || survey?.description || t('Evaluates core symptoms of focus, impulsivity, and executive function across key symptom areas to support your personal clarity and self-awareness.');
    const totalQuestions = survey?.totalQuestions || survey?.questions?.length || 18;
    const estimatedTime = survey?.estimatedTimeMinutes || 3;

    return (
        <div className="survey-wrapper-bs min-vh-100 d-flex flex-column bg-canvas-slate">
            <div className="container-fluid flex-grow-1 d-flex align-items-center justify-content-center px-3 py-5">
                <div className="w-100" style={{ maxWidth: '42rem' }}>
                    <div className="card survey-card-bs border-0 shadow-sm p-4 p-md-5 rounded-4 bg-white position-relative overflow-hidden">
                        
                        {/* Ambient Glow Background Accents */}
                        <div className="glow-accent glow-top-right"></div>
                        <div className="glow-accent glow-bottom-left"></div>

                        <div className="d-flex flex-column align-items-center text-center position-relative z-1">
                            
                            {/* Top Badge */}
                            <div className="badge-intro px-3 py-1.5 rounded-pill shadow-sm mb-4 d-inline-flex align-items-center gap-2">
                                <FontAwesomeIcon icon={faWandMagicSparkles} className="text-purple" />
                                <span className="small fw-semibold text-purple">
                                    {t(`Free Self-Assessment • ~${estimatedTime} Min`)}
                                </span>
                            </div>

                            {/* Title & Description */}
                            <div className="mb-4 max-w-xl">
                                <h1 className="headline-title fw-bold text-dark mb-2">
                                    {title} {t('Self-Report Screener')}
                                </h1>
                                <p className="text-secondary body-text mb-0">
                                    {description}
                                </p>
                            </div>

                            {/* Feature Cards Stack */}
                            <div className="w-100 d-flex flex-column gap-3 mb-4 text-start">
                                
                                {/* Feature 1 */}
                                <div className="feature-card d-flex align-items-start gap-3 p-3 rounded-4 bg-light transition-all">
                                    <div className="icon-wrapper rounded-3 bg-lavender d-flex align-items-center justify-content-center flex-shrink-0">
                                        <FontAwesomeIcon icon={faStopwatch} className="text-purple" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <span className="fw-semibold text-dark fs-6">{t('Quick & Simple')}</span>
                                            <span className="badge bg-purple-subtle text-purple rounded-pill px-2 py-1 small fw-medium">
                                                {totalQuestions} {t('Prompts')}
                                            </span>
                                        </div>
                                        <p className="text-secondary small mb-0">
                                            {t('Standardized questions crafted to capture daily attention patterns in under 3 minutes.')}
                                        </p>
                                    </div>
                                </div>

                                {/* Feature 2 */}
                                <div className="feature-card d-flex align-items-start gap-3 p-3 rounded-4 bg-light transition-all">
                                    <div className="icon-wrapper rounded-3 bg-lavender d-flex align-items-center justify-content-center flex-shrink-0">
                                        <FontAwesomeIcon icon={faShieldHalved} className="text-purple" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <span className="fw-semibold text-dark fs-6">{t('100% Anonymous')}</span>
                                            <span className="badge bg-success-subtle text-success rounded-pill px-2 py-1 small fw-medium">
                                                {t('Client-Side')}
                                            </span>
                                        </div>
                                        <p className="text-secondary small mb-0">
                                            {t('No account required. Responses stay inside your local browser sandbox and are never sent to a tracking database.')}
                                        </p>
                                    </div>
                                </div>

                                {/* Feature 3 */}
                                <div className="feature-card d-flex align-items-start gap-3 p-3 rounded-4 bg-light transition-all">
                                    <div className="icon-wrapper rounded-3 bg-lavender d-flex align-items-center justify-content-center flex-shrink-0">
                                        <FontAwesomeIcon icon={faChartLine} className="text-purple" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <span className="fw-semibold text-dark fs-6">{t('Visual Insights')}</span>
                                            <span className="badge bg-purple-subtle text-purple rounded-pill px-2 py-1 small fw-medium">
                                                {t('Instant Chart')}
                                            </span>
                                        </div>
                                        <p className="text-secondary small mb-0">
                                            {t('Immediate multi-dimensional score breakdown, executive function profiles, and practical next steps.')}
                                        </p>
                                    </div>
                                </div>

                            </div>

                            {/* Non-Diagnostic Notice */}
                            <div className="notice-card w-100 p-3 rounded-4 bg-purple-light-bg mb-4 d-flex align-items-start gap-3 text-start border-0">
                                <div className="notice-icon-wrapper rounded-3 bg-white d-flex align-items-center justify-content-center flex-shrink-0 shadow-sm">
                                    <FontAwesomeIcon icon={faCircleInfo} className="text-secondary" />
                                </div>
                                <div className="min-w-0">
                                    <span className="fw-semibold text-dark small d-block mb-1">
                                        {t('Non-Diagnostic Notice')}
                                    </span>
                                    <p className="text-secondary extra-small mb-0 leading-relaxed">
                                        {t('This screener measures symptom intensity for self-awareness and personal reflection. It is not a formal clinical diagnosis or a replacement for consultation with a licensed healthcare professional.')}
                                    </p>
                                </div>
                            </div>

                            {/* Start Action Button */}
                            <div className="w-100 d-flex flex-column align-items-center gap-3">
                                <button
                                    type="button"
                                    onClick={onStart}
                                    className="btn btn-purple-gradient w-100 py-3 rounded-4 fw-semibold text-white d-flex align-items-center justify-content-center gap-2 shadow"
                                >
                                    <span>{t('Start Free Screener')}</span>
                                    <FontAwesomeIcon icon={faArrowRight} className="btn-arrow" />
                                </button>

                                {/* Micro Trust Footer */}
                                <div className="d-flex flex-wrap align-items-center justify-content-center gap-2 text-muted extra-small">
                                    <span className="d-inline-flex align-items-center gap-1">
                                        <FontAwesomeIcon icon={faCircleCheck} className="text-success" />
                                        {t('Confidential')}
                                    </span>
                                    <span>•</span>
                                    <span>{t('Instant scoring')}</span>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SurveyIntro;